import { useSyncExternalStore } from "react";
import { BREAKPOINTS } from "@remi/constants";

type Screen = "sm" | "md" | "lg" | "xl";

interface MediaState {
  isMobile: boolean;
  isTablet: boolean;
  isLaptop: boolean;
  isDesktop: boolean;
  screen: Screen;
}

const STATES = {
  sm: { isMobile: true, isTablet: false, isLaptop: false, isDesktop: false, screen: "sm" },
  md: { isMobile: false, isTablet: true, isLaptop: false, isDesktop: false, screen: "md" },
  lg: { isMobile: false, isTablet: false, isLaptop: true, isDesktop: false, screen: "lg" },
  xl: { isMobile: false, isTablet: false, isLaptop: false, isDesktop: true, screen: "xl" },
} as const satisfies Record<Screen, MediaState>;

const getMediaState = (width: number): MediaState => {
  if (width < BREAKPOINTS.MD) {
    return STATES.sm;
  }
  if (width < BREAKPOINTS.LG) {
    return STATES.md;
  }
  if (width < BREAKPOINTS.XL) {
    return STATES.lg;
  }
  return STATES.xl;
};

let cachedState: MediaState = STATES.xl;
let initialized = false;

// Read width lazily on first snapshot rather than at module load: an eager module-load read
// can cache a pre-settle width no later resize corrects, opening drawers as a desktop bottom
// sheet (DES-2618). Unsubscribe resets it so a resize landing while unmounted is picked up on the
// next mount's first snapshot read.
const getSnapshot = (): MediaState => {
  if (!initialized && typeof window !== "undefined") {
    cachedState = getMediaState(window.innerWidth);
    initialized = true;
  }
  return cachedState;
};

const getServerSnapshot = (): MediaState => STATES.xl;

const listeners = new Set<() => void>();
let resizeCleanup: (() => void) | null = null;

const subscribe = (callback: () => void): (() => void) => {
  listeners.add(callback);

  if (listeners.size === 1) {
    // getSnapshot runs at render, subscribe at commit: a resize in that gap reaches no listener and
    // `initialized` blocks a re-read, so React's consistency check compares stale to stale and the
    // wrong value sticks until the next resize.
    cachedState = getMediaState(window.innerWidth);

    const onResize = () => {
      const next = getMediaState(window.innerWidth);
      if (next !== cachedState) {
        cachedState = next;
        for (const listener of listeners) {
          listener();
        }
      }
    };
    window.addEventListener("resize", onResize);
    resizeCleanup = () => window.removeEventListener("resize", onResize);
  }

  return () => {
    listeners.delete(callback);
    if (listeners.size === 0 && resizeCleanup) {
      resizeCleanup();
      resizeCleanup = null;
      initialized = false;
    }
  };
};

export const useMedia = (): MediaState => {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
};
